import React, { useEffect } from 'react';
import { gsap } from 'gsap';
import { texteATrousData } from '../assets/data/mission2/texteATrous';
import '../styles/Textetrou.css';
import MissionHeader from '../components/MissionHeader';
import MainButton from '../components/MainButton';
import { useNavigate, useParams } from 'react-router-dom';
import { useTimer } from '../contextes/useTimer';

type Trou = { position: number; answer: string };
type Option = { index: number; answer: string; isCorrect: boolean };
type FilledState = Record<number, { value: string; isCorrect: boolean; optionKey: string } | null>;

function parseWithHoles(raw: string) {
    const parts: Array<{ type: 'text'; value: string } | { type: 'hole'; id: number }> = [];
    const regex = /\[\[(\d+)\]\]/g;
    let last = 0;
    let m: RegExpExecArray | null;

    while ((m = regex.exec(raw))) {
        if (m.index > last) parts.push({ type: 'text', value: raw.slice(last, m.index) });
        parts.push({ type: 'hole', id: Number(m[1]) });
        last = regex.lastIndex;
    }
    if (last < raw.length) parts.push({ type: 'text', value: raw.slice(last) });
    return parts;
}

type OptionStatus = Record<
    string,
    { state: 'available' | 'wrong' | 'correct'; lastResult?: 'good' | 'bad' }
>;

export default function TextTrou() {
    const navigate = useNavigate();
    const parts = React.useMemo(() => parseWithHoles(texteATrousData.text), []);
    const [showFeedBack, setShowFeedBack] = React.useState(false);
    const [showSources, setShowSources] = React.useState(false);
    const [showIntro, setShowIntro] = React.useState(true);
    const [currentCount, setCurrentCount] = React.useState(0);
    const optionsContainerRef = React.useRef<HTMLDivElement | null>(null);
    const feedbackContainerRef = React.useRef<HTMLDivElement | null>(null);
    // --- Hooks de routing (toujours en haut, avant tout return) ---
    const { missionNumber } = useParams<{ missionNumber: string }>();

    const { stopTimer } = useTimer();
    const totalCount = React.useMemo(() => {
        let count = 0;
        for (const p of parts) {
            if (p.type === 'hole') count++;
        }
        return count;
    }, [parts]);

    const holeIds = React.useMemo(() => {
        const ids: number[] = [];
        const seen = new Set<number>();
        for (const p of parts) {
            if (p.type === 'hole' && !seen.has(p.id)) {
                seen.add(p.id);
                ids.push(p.id);
            }
        }
        return ids;
    }, [parts]);

    const holes: Trou[] = React.useMemo(
        () =>
            (texteATrousData.trous as any[])
                .filter((t) => t.isCorrect === true)
                .map((t) => ({ position: t.position, answer: t.answer })),
        [],
    );

    const expectedByHole = React.useMemo(() => {
        const map = new Map<number, string>();
        holes.forEach((h) => map.set(h.position, h.answer));
        return map;
    }, [holes]);

    const options: Option[] = React.useMemo(
        () =>
            (texteATrousData.trous as any[]).map((t) => ({
                index: t.index,
                answer: t.answer,
                isCorrect: Boolean(t.isCorrect),
            })),
        [],
    );

    const [filled, setFilled] = React.useState<FilledState>({});
    const [optionStatus, setOptionStatus] = React.useState<OptionStatus>({});

    // Refs pour animations
    const holeRefs = React.useRef<Record<number, HTMLSpanElement | null>>({});
    const topRef = React.useRef<HTMLDivElement | null>(null);

    const makeOptionKey = (opt: Option) => `${opt.index}__${opt.answer}`;

    const nextHoleToFill = React.useMemo(() => {
        for (const id of holeIds) {
            const st = filled[id];
            if (!st?.value) return id;
        }
        return null;
    }, [filled, holeIds]);

    const animateWrong = (holeId: number) => {
        const el = holeRefs.current[holeId];
        if (el) {
            gsap.fromTo(
                el,
                { x: 0 },
                { x: 8, duration: 0.06, yoyo: true, repeat: 5, ease: 'power1.inOut' },
            );
        }
        if (topRef.current) {
            gsap.fromTo(
                topRef.current,
                { scale: 1 },
                { scale: 1.05, duration: 0.12, yoyo: true, repeat: 1 },
            );
        }
    };

    const animateCorrect = (holeId: number) => {
        const el = holeRefs.current[holeId];
        if (!el) return;

        // petit flash vert du trou
        gsap.fromTo(el, { scale: 1 }, { scale: 1.03, duration: 0.12, yoyo: true, repeat: 1 });
    };

    const animateEnd = () => {
        const container = optionsContainerRef.current;

        if (!container) return;

        gsap.to(container.querySelectorAll('.textetrou-interactive-text'), {
            opacity: 0,
            duration: 0.5,
            ease: 'power2.inOut',
            stagger: 0.1,
        });
    };

    const animateFeedBack = () => {
        const container = feedbackContainerRef.current;

        if (!container) return;
        gsap.fromTo(
            container.querySelectorAll('.textetrou-feedback-content'),

            { opacity: 0, y: 30 },
            { opacity: 1, y: 0, duration: 0.8, ease: 'power2.out', stagger: 0.1 },
        );
    };

    const placeOptionInNextHole = (opt: Option) => {
        if (nextHoleToFill == null) return;

        const holeId = nextHoleToFill;
        const expected = expectedByHole.get(holeId) ?? '';
        const ok = opt.answer.trim() === expected.trim();
        const optionKey = makeOptionKey(opt);

        // Remplit le trou
        setFilled((prev) => ({
            ...prev,
            [holeId]: { value: opt.answer, isCorrect: ok, optionKey },
        }));

        // Feedback + état option
        if (ok) {
            const temporaryCount = currentCount + 1;
            console.log('temporaryCount:', temporaryCount);
            setCurrentCount(temporaryCount);
            animateCorrect(holeId);

            // ✅ si correct: l'option DISPARAIT (state=correct)
            setOptionStatus((prev) => ({
                ...prev,
                [optionKey]: { state: 'correct', lastResult: 'good' },
            }));
        } else {
            // (optionnel) tu peux la griser PENDANT l'animation pour montrer "pris"
            setOptionStatus((prev) => ({
                ...prev,
                [optionKey]: { state: 'wrong', lastResult: 'bad' },
            }));

            animateWrong(holeId);

            // Après l'animation : le texte disparaît du haut + revient en bas (available)
            window.setTimeout(() => {
                setOptionStatus((prev) => ({
                    ...prev,
                    [optionKey]: { state: 'available' }, // opacité normale + re-cliquable
                }));

                // IMPORTANT: on vide aussi le trou (puisque c'était faux)
                setFilled((prev) => ({ ...prev, [holeId]: null }));
            }, 600); // 0.06*5*2 ≈ 0.6s max; 450ms marche bien, ajuste si tu veux
        }
    };

    const getHoleClass = (holeId: number) => {
        const state = filled[holeId];
        if (!state?.value) return 'whiteCadre';
        return `whiteCadre ${state.isCorrect ? 'whiteBlue' : 'whiteRed'}`;
    };

    const getOptionClass = (opt: Option) => {
        const key = makeOptionKey(opt);
        const st = optionStatus[key];

        const base = 'textetrou-interactive-text';
        const used = st?.state === 'wrong' ? ' isUsed' : ''; // seulement si faux -> faible opacité
        const color = st?.state === 'wrong' ? ' optBad' : st?.state === 'correct' ? ' optGood' : '';

        return `${base}${used}${color}`;
    };

    const isOptionClickable = (opt: Option) => {
        const key = makeOptionKey(opt);
        const st = optionStatus[key];
        // si correct => cachée, donc pas cliquable de toute façon
        // si wrong => tu peux décider de la laisser re-cliquable ou non; là: re-cliquable OK
        return st?.state !== 'correct';
    };

    useEffect(() => {
        console.log(currentCount, totalCount);
        if (currentCount === totalCount) {
            animateEnd();
            window.setTimeout(() => {
                setShowFeedBack(true);
                stopTimer();
                animateFeedBack();
            }, 3000);
        }
    }, [currentCount, totalCount]);

    return (
        <div className='textetrou-container'>
            {/* --- Message Initial  --- */}
            {showIntro && (
                <div className='textetrou-intro'>
                    <MissionHeader
                        showBackButton={false}
                        showSettingsButton={false}
                        showCloseButton={true}
                        logoVariant={'default'}
                        closeButtonVariant={'white'}
                    />

                    <div className='textetrou-intro-text'>
                        <h1 className='textetrou-intro-title'>{'Décryptez'}</h1>
                        <h1 className='textetrou-intro-title'>{'le texte'}</h1>
                        <p>
                            Complétez le texte à trous en cliquant sur les mots qui vous semblent
                            être à la bonne place.
                        </p>
                    </div>
                    <img
                        src='./images/agent-image-2.png'
                        alt='Image agent'
                        className='textetrou-feedback-image textetrou-feedback-content'
                    />
                    <div className='textetrou-feedback-button textetrou-feedback-content'>
                        <MainButton
                            text='Commencer'
                            onClick={() => {
                                setShowIntro(false);
                            }}
                        />
                    </div>
                </div>
            )}
            {/* --- Header texte avec trous --- */}
            {!showFeedBack && !showIntro && (
                <div className='textetrou-header-container'>
                    <MissionHeader
                        showBackButton={false}
                        showSettingsButton={false}
                        showCloseButton={false}
                        logoVariant={'dark'}
                        closeButtonVariant={'tertiary'}
                    />
                </div>
            )}

            {/* --- Texte avec trous --- */}
            <div className='textetrou-text-container'>
                {parts.map((p, i) => {
                    if (p.type === 'text')
                        return <React.Fragment key={i}>{p.value}</React.Fragment>;

                    const holeId = p.id;
                    const state = filled[holeId];

                    return (
                        <span
                            key={i}
                            ref={(el) => {
                                holeRefs.current[holeId] = el;
                            }}
                            className={getHoleClass(holeId)}
                            role='button'
                            tabIndex={0}
                            // onClick={() => {
                            //     // Si rouge -> clic = vider + libérer l'option en bas
                            //     if (state?.value && state.isCorrect === false) {
                            //         clearHoleAndFreeOption(holeId);
                            //     } else if (state?.value && state.isCorrect === true) {
                            //         // si vert on stocke dans un int pour compter les bonnes réponses
                            //         const temporaryCount = currentCount + 1;
                            //         console.log('temporaryCount:', temporaryCount);
                            //         setCurrentCount(temporaryCount);
                            //     }
                            // }}
                            aria-label={`Trou ${holeId}`}
                        >
                            {state?.value ? (
                                state.value
                            ) : (
                                <span className='placeholder'>(...)</span>
                            )}
                        </span>
                    );
                })}
            </div>

            {/* --- Source --- */}
            <div className='textetrou-source'>
                <p>EMA/66027/2024. Assessment report du 25 janvier 2024</p>
            </div>

            {/* --- Options --- */}
            <div
                className='textetrou-interactive-area'
                ref={optionsContainerRef}
            >
                {options.map((opt) => {
                    const clickable = isOptionClickable(opt);

                    return (
                        <div
                            key={`${opt.index}-${opt.answer}`}
                            className={getOptionClass(opt) + ' textetrou-interactive-text'}
                            onClick={() => {
                                if (!clickable) return;
                                placeOptionInNextHole(opt);
                            }}
                            role='button'
                            tabIndex={0}
                            aria-disabled={!clickable}
                        >
                            {opt.answer}
                        </div>
                    );
                })}
            </div>

            { showSources && (
                <div className='textetrou-sources'>
                    <h2 className='sources-title'>Sources</h2>
                    <br />

                    <p className='sources-text'><b>Carte 50 :</b> pour le graphique - Fréquence des méningites à pneumocoque en fonction de l’âge chez les enfants de moins de 2 ans en France hexagonale en 2023</p>
                    <br />
                    <p className='sources-text'><b>Carte 63 :</b> Rapport annuel d’activité 2024 du CNRP - Année d’exercice 2023</p>
                </div>
            )}

            {/* --- Feedback final --- */}
            {showFeedBack && (
                <div
                    className='textetrou-feedback'
                    ref={feedbackContainerRef}
                >
                    <div className='textetrou-feedback-header-container textetrou-feedback-content'>
                        <MissionHeader
                            showBackButton={false}
                            showSettingsButton={false}
                            showCloseButton={false}
                            logoVariant={'default'}
                            closeButtonVariant={'tertiary'}
                        />
                    </div>
                    <img
                        src='./images/agent-image-4.png'
                        alt='Image agent'
                        className='textetrou-feedback-image textetrou-feedback-content'
                    />
                    <div className='textetrou-feedback-text textetrou-feedback-content'>
                        <p>Voilà qui est plus clair !</p>
                        <p>
                            Maintenant Harvey, il est temps de prendre rendez-vous pour ta première
                            injection !
                        </p>
                        <p>
                            <b>
                                Encore une mission <br /> rondement menée !
                            </b>
                        </p>
                    </div>
                    <div className='textetrou-feedback-button textetrou-feedback-content'>
                        {!showSources && (
                            <MainButton
                                text='Sources'
                                onClick={() => {
                                    setShowSources(true);
                                }}
                            />
                        )}
                        <MainButton
                            text='Terminer'
                            onClick={() => {
                                navigate(`/mission/${missionNumber}/endgame`, {
                                    replace: true,
                                    state: { hasWon: true },
                                });
                            }}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}
